import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Modal from "../../Components/Modal";
import { QRCodeCanvas } from "qrcode.react";
import GuestLayout from "../../Layouts/GuestLayout";
import { Button } from '../../shadcn/ui/button';

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [qrUrl, setQrUrl] = useState("");
  const [otp, setOtp] = useState(new Array(6).fill("")); // Initialize OTP as an array
  const [modalOpen, setModalOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, login, generate2FASecret, validate2FA, send2FAQRCode } = useAuth();

  if (user) {
    return <Navigate to="/" replace />;
  }

  if (user && !user.two_factor_enabled) {
    return <Navigate to="/setup-2fa" replace />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await login(email, password);

      // Redirect user after successful login
      const redirectPath = location.state?.from?.pathname || "/";
      navigate(redirectPath, { replace: true });
    } catch (err) {
      if (
        err.response &&
        err.response.status === 403 &&
        err.response.data.two_factor_enabled === false &&
        err.response.data.two_factor_verified === false
      ) {
        try {
          const { qr_code_url } = await generate2FASecret();
          setQrUrl(qr_code_url); // Set the QR code URL
          setModalOpen(true); // Open the modal for setup
        } catch (fetchError) {
          console.error("Error fetching 2FA secret:", fetchError);
          setError("Αποτυχία δημιουργίας μυστικού 2FA. Δοκιμάστε ξανά.");
        }
      } else if (
        err.response &&
        err.response.status === 403 &&
        err.response.data.two_factor_enabled === true &&
        err.response.data.two_factor_verified === false
      ) {
        setModalOpen(true); // Open the modal for OTP input
      } else {
        setError("Μη έγκυρα διαπιστευτήρια. Δοκιμάστε ξανά.");
      }
    }
  };


  const handleChange = (value, index) => {
    if (value.length > 1) return; // Ensure only one digit is entered
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move focus to the next input
    if (value && index < otp.length - 1) {
      document.getElementById(`otp-input-${index + 1}`).focus();
    }
  };

  const handleTwoFactorSubmit = async (e) => {
    setError("");
    setSuccess("");
    e.preventDefault();
    const otpCode = otp.join(""); // Combine the OTP array into a single string
    try {
      await validate2FA(otpCode);
      setModalOpen(false);
      const redirectPath = location.state?.from?.pathname || "/";
      navigate(redirectPath, { replace: true });
    } catch (err) {
      setError("Μη έγκυρο OTP. Δοκιμάστε ξανά.");
    }
  };

  const handleSendQRCode = async (user) => {
    setError("");
    setSuccess("");
    try {
      const message = await send2FAQRCode(email);
      setSuccess(message);
    } catch (err) {
      setError("Αποτυχία αποστολής του κωδικού QR μέσω email. Δοκιμάστε ξανά.");
    }
  };

  return (
    <GuestLayout>
      <div className="flex flex-col items-center justify-center h-screen">
        <form
          onSubmit={handleLogin}
          className="w-full max-w-md bg-white p-6 rounded shadow-md"
        >
          <h1 className="text-2xl mb-4">Σύνδεση</h1>
          {error && <div className="text-red-500 mb-4">{error}</div>}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full p-2 mb-4 border rounded"
            required
          />
          <input
            type="password"
            placeholder="Κωδικός πρόσβασης"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full p-2 mb-4 border rounded"
            required
          />
          <Button type="submit">
            Σύνδεση
          </Button>
          <div className="mt-4 text-center">
            <Link
              to="/forgot-password"
              className="text-sm text-blue-500 hover:text-blue-700"
            >
              Ξεχάσατε τον κωδικό πρόσβασής σας;
            </Link>
          </div>
        </form>

        <Modal show={modalOpen} onClose={() => setModalOpen(false)} maxWidth="lg">
          <div className="p-6">
            {!user?.two_factor_enabled ? (
              <h1 className="text-2xl mb-4">Ρύθμιση ελέγχου ταυτότητας δύο παραγόντων</h1>
            ) : (
              <h1 className="text-2xl mb-4">Επαλήθευση δύο παραγόντων</h1>
            )}

            {error && <div className="text-red-500 mb-4">{error}</div>}
            {success && <div className="text-green-500 mb-4">{success}</div>}

            {/* Show QR Code for Setup */}
            {qrUrl && !user?.two_factor_enabled && (
              <>
                <p className="mb-4">
                  Για να ρυθμίσετε τον έλεγχο ταυτότητας δύο παραγόντων:
                </p>
                <ol className="list-decimal list-inside mb-4 text-left">
                  <li>
                    Κατεβάστε την εφαρμογή{" "}
                    <a
                      href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en&gl=US"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 underline"
                    >
                      Google Authenticator (Android)
                    </a>{" "}
                    ή{" "}
                    <a
                      href="https://apps.apple.com/us/app/google-authenticator/id388497605"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 underline"
                    >
                      Google Authenticator (iOS)
                    </a>.
                  </li>
                  <li>Ανοίξτε την εφαρμογή και επιλέξτε "Σάρωση QR κώδικα".</li>
                  <li>Σαρώστε τον παρακάτω κωδικό QR.</li>
                  <li>Εισαγάγετε τον 6-ψήφιο κωδικό που εμφανίζεται στην εφαρμογή.</li>
                </ol>
                <div className="flex justify-center mb-4">
                  <QRCodeCanvas value={qrUrl} size={200} includeMargin={true} />
                </div>
              </>
            )}

            {/* OTP Verification Form */}
            <form onSubmit={handleTwoFactorSubmit}>
              <div className="flex justify-center gap-2 mb-4">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-input-${index}`}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleChange(e.target.value, index)}
                    className="w-12 h-12 text-center border rounded text-lg"
                    onKeyDown={(e) => {
                      if (e.key === "Backspace" && !otp[index] && index > 0) {
                        document.getElementById(`otp-input-${index - 1}`).focus();
                      }
                    }}
                  />
                ))}
              </div>

              {!user?.two_factor_enabled ? (
                <>
                  <Button type="submit" className="w-full mb-2 text-white">
                  Επαλήθευση OTP
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    type="submit"
                    className="w-full mb-2 text-white"
                  >
                    Επαληθεύω
                  </Button>

                  {/* Resend QR Code Button */}
                  <Button
                    type="button"
                    onClick={handleSendQRCode}
                    className="w-full bg-gray-500 text-white"
                  >
                    Στείλτε ξανά τον κωδικό QR στο email
                  </Button>
                </>
              )}


            </form>
          </div>
        </Modal>

      </div>
    </GuestLayout>
  );
}
